import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, BACKEND_URL } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Crown, Calendar, Play, Bell, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [calendly, setCalendly] = useState("");
  const [slack, setSlack] = useState("");
  const [slackBusy, setSlackBusy] = useState(false);
  const [ttsText, setTtsText] = useState("Hey — quick note from AiPipe. Tuesday 10am works well if that's still on the table.");
  const [ttsBusy, setTtsBusy] = useState(false);
  const isFull = user?.full_access || user?.plan === "scale";

  useEffect(() => {
    setCalendly(user?.calendly_url || "");
    setSlack(user?.slack_webhook_url || "");
  }, [user]);

  const grant = async () => {
    setBusy(true);
    try {
      await api.post("/account/grant-full-access");
      toast.success("You now have full Scale access — all features unlocked");
      await refresh();
    } catch {
      toast.error("Could not grant access");
    } finally {
      setBusy(false);
    }
  };

  const saveCalendly = async () => {
    if (!calendly.startsWith("http")) return toast.error("Enter a full URL");
    await api.patch("/account/calendly", { calendly_url: calendly });
    toast.success("Calendly URL saved");
    await refresh();
  };

  const saveSlack = async () => {
    setSlackBusy(true);
    try {
      await api.patch("/account/slack-webhook", { slack_webhook_url: slack });
      toast.success(slack ? "Slack webhook saved" : "Slack webhook cleared");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSlackBusy(false);
    }
  };

  const testSlack = async () => {
    setSlackBusy(true);
    try {
      const { data } = await api.post("/account/slack-test");
      if (data.ok) toast.success("Test alert sent — check your Slack channel");
      else toast.error(`Slack rejected the ping (${data.status || data.error || "unknown"})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setSlackBusy(false);
    }
  };

  const previewVoice = async () => {
    setTtsBusy(true);
    try {
      const { data } = await api.post("/ai/voice-note", { text: ttsText, voice: "nova", model: "tts-1" });
      new Audio(`${BACKEND_URL}${data.url}`).play();
      toast.success("Playing preview");
    } catch {
      toast.error("Voice generation failed");
    } finally {
      setTtsBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-view">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings & integrations</h1>
        <p className="mt-1 text-sm text-slate-400">Manage your workspace, integrations and deliverability.</p>
      </div>

      <div className="rounded-2xl border border-sky-400/30 bg-gradient-to-br from-sky-500/10 to-transparent p-6" data-testid="settings-access-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-300" />
              <div className="text-sm font-semibold">Free full-access grant</div>
            </div>
            <p className="mt-1 text-xs text-slate-400 max-w-md">Unlock the entire Scale tier — unlimited agents, prospecting, sequences and AI drafts, at no cost.</p>
          </div>
          {isFull ? (
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/20"><CheckCircle2 className="w-3 h-3 mr-1" /> Scale · complimentary</Badge>
          ) : (
            <Button onClick={grant} disabled={busy} data-testid="settings-grant-access-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full">
              {busy ? "Unlocking…" : "Unlock full access"}
            </Button>
          )}
        </div>
      </div>

      {/* Calendly */}
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 space-y-3" data-testid="settings-calendly-card">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-sky-300" />
          <div className="text-sm font-semibold">Calendly URL</div>
        </div>
        <p className="text-xs text-slate-400">Drops into AI replies whenever a prospect signals positive intent.</p>
        <div className="flex gap-2">
          <Input value={calendly} onChange={(e) => setCalendly(e.target.value)} placeholder="https://calendly.com/yourname/30min" data-testid="settings-calendly-input" className="bg-white/[0.03] border-white/10" />
          <Button onClick={saveCalendly} data-testid="settings-calendly-save" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold">Save</Button>
        </div>
      </div>

      {/* Slack alerts */}
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 space-y-3" data-testid="settings-slack-card">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-violet-300" />
            <div className="text-sm font-semibold">Slack alerts</div>
          </div>
          {user?.slack_webhook_url && (
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/20 text-[10px]">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-400">
          Every rule-matched pricing visit pings your channel in real time — reply within 5 min and conversion jumps 21×.
          Paste a Slack{" "}
          <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:underline inline-flex items-center gap-0.5">
            incoming webhook URL <ExternalLink className="w-3 h-3" />
          </a>.
        </p>
        <div className="flex gap-2">
          <Input
            value={slack}
            onChange={(e) => setSlack(e.target.value)}
            placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXX"
            data-testid="settings-slack-input"
            className="bg-white/[0.03] border-white/10 font-mono text-xs"
          />
          <Button onClick={saveSlack} disabled={slackBusy} data-testid="settings-slack-save" className="bg-violet-500 hover:bg-violet-400 text-slate-950 font-semibold">
            {slackBusy ? "…" : "Save"}
          </Button>
          <Button onClick={testSlack} disabled={slackBusy || !user?.slack_webhook_url} data-testid="settings-slack-test" variant="outline" className="border-white/10 bg-white/[0.02]">
            Test
          </Button>
        </div>
      </div>

      {/* Voice preview */}
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 space-y-3" data-testid="settings-voice-card">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-emerald-300" />
          <div className="text-sm font-semibold">AI voice preview (OpenAI TTS)</div>
        </div>
        <p className="text-xs text-slate-400">Generated on demand for LinkedIn voice-note steps in your sequences.</p>
        <div className="flex gap-2">
          <Input value={ttsText} onChange={(e) => setTtsText(e.target.value)} data-testid="settings-voice-text" className="bg-white/[0.03] border-white/10" />
          <Button onClick={previewVoice} disabled={ttsBusy} data-testid="settings-voice-play" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
            {ttsBusy ? "Generating…" : "Play"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 space-y-4">
        <div className="text-sm font-semibold">Workspace</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Name</div><div className="mt-1">{user?.name}</div></div>
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Email</div><div className="mt-1">{user?.email}</div></div>
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Plan</div><div className="mt-1"><Badge className="bg-sky-500/15 text-sky-200 border-sky-400/20 capitalize">{user?.plan || "trial"}</Badge></div></div>
          <div><div className="text-xs text-slate-500 uppercase tracking-widest">Billing</div><div className="mt-1 capitalize">{user?.billing || "—"}</div></div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6">
        <div className="text-sm font-semibold mb-4">Integrations</div>
        <div className="space-y-3">
          {["HubSpot CRM", "Salesforce", "Gmail", "Outlook", "LinkedIn Sales Navigator", "Calendly", "Slack", "Hunter.io"].map((n) => (
            <div key={n} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-b-0 last:pb-0">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <div>
                  <div className="text-sm">{n}</div>
                  <div className="text-xs text-slate-500">{n === "Hunter.io" ? "Prospecting API · add HUNTER_API_KEY in backend/.env" : "Two-way sync · ready"}</div>
                </div>
              </div>
              <Switch data-testid={`settings-integration-${n.replace(/\s+/g, "-").toLowerCase()}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6">
        <div className="text-sm font-semibold mb-2">Deliverability</div>
        <div className="text-xs text-slate-400">Domain warmup running · 3/3 mailboxes healthy · 0 bounces last 7d</div>
      </div>
    </div>
  );
}
